import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Update as UpdateIcon,
  CheckCircle as CheckIcon,
  Settings as SetupIcon,
  Delete as DeleteIcon,
  RestartAlt as ResetIcon,
  Edit as EditIcon,
  MonitorHeart as HealthIcon,
  PauseCircle as PauseIcon,
  PlayCircle as PlayIcon,
} from '@mui/icons-material';
import {
  fetchEncoders,
  fetchEncoderStats,
  createEncoder,
  checkAllEncodersHealth,
} from '../store/slices/encodersSlice';
import EncoderSetupDialog from '../components/EncoderSetupDialog';
import api from '../services/api';

function Encoders() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { encoders, stats } = useSelector((state) => state.encoders);
  const { userInfo } = useSelector((state) => state.auth);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [selectedEncoder, setSelectedEncoder] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [encodersWithStatus, setEncodersWithStatus] = useState([]);
  const [updating, setUpdating] = useState({});
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    hostname: '',
    ip_address: '',
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [encoderToDelete, setEncoderToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEncoder, setEditingEncoder] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    hostname: '',
    ip_address: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch encoders through Redux
      dispatch(fetchEncoders());
      dispatch(fetchEncoderStats());
      
      // Also fetch update status
      const statusResponse = await api.get('/api/encoder-updates/status');
      const statusData = statusResponse.data;
      
      if (statusData.encoders && statusData.summary) {
        // New format with encoders and summary
        const statusMap = {};
        statusData.encoders.forEach(status => {
          statusMap[status.id] = status;
        });
        
        // We'll merge this with Redux encoders data in a separate effect
        setUpdateStatus(statusData.summary);
      }
    } catch (_error) {
      // Failed to fetch data - component will show loading state
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchData();
    
    // Real-time updates are handled by WebSocket events
    // The Redux store is automatically updated when encoder events are received
    // No need for polling anymore
    
    // Listen for encoder update events
    const handleEncoderUpdated = (_event) => {
      // Refresh encoder data to get new version
      fetchData();
    };
    
    // Also refresh when encoder comes back online (after restart from update)
    const handleEncoderOnline = (event) => {
      // Check if this encoder was being updated
      const encoderId = event.detail?.id;
      if (encoderId && updating[encoderId]) {
        // Small delay to ensure the encoder has fully registered
        setTimeout(() => {
          fetchData();
          setUpdating(prev => ({ ...prev, [encoderId]: false }));
        }, 1000);
      }
    };
    
    window.addEventListener('encoder-updated', handleEncoderUpdated);
    window.addEventListener('encoder-online', handleEncoderOnline);
    
    return () => {
      window.removeEventListener('encoder-updated', handleEncoderUpdated);
      window.removeEventListener('encoder-online', handleEncoderOnline);
    };
  }, [fetchData, updating]);

  // Merge encoder data with update status
  useEffect(() => {
    const mergeData = async () => {
      if (encoders.length > 0) {
        try {
          const statusResponse = await api.get('/api/encoder-updates/status');
          const statusData = statusResponse.data;
          
          if (statusData.encoders) {
            const statusMap = {};
            statusData.encoders.forEach(status => {
              statusMap[status.id] = status;
            });
            
            const merged = encoders.map(encoder => ({
              ...encoder,
              update_status: statusMap[encoder.id]?.needs_update ? 'update_available' : 'up_to_date',
              agent_version: statusMap[encoder.id]?.current_version || 'unknown'
            }));
            
            setEncodersWithStatus(merged);
            if (statusData.summary) {
              setUpdateStatus(statusData.summary);
            }
          } else {
            setEncodersWithStatus(encoders);
          }
        } catch (_error) {
          // Failed to get update status - will show encoders without update info
          setEncodersWithStatus(encoders);
        }
      }
    };
    
    mergeData();
  }, [encoders]);

  const handleRefresh = () => {
    fetchData();
    dispatch(checkAllEncodersHealth());
  };

  const handleSetup = (encoder) => {
    setSelectedEncoder(encoder);
    setSetupDialogOpen(true);
  };

  const handleSubmit = () => {
    dispatch(createEncoder(formData));
    setDialogOpen(false);
    setFormData({
      name: '',
      hostname: '',
      ip_address: '',
    });
  };

  const handleUpdateEncoderAgent = async (encoderId) => {
    setUpdating(prev => ({ ...prev, [encoderId]: true }));
    try {
      await api.post(`/api/encoder-updates/${encoderId}/update`);
      // Refresh data after update
      await fetchData();
    } catch (_error) {
      // Failed to update encoder - update button will re-enable
    } finally {
      setUpdating(prev => ({ ...prev, [encoderId]: false }));
    }
  };

  const handleUpdateAll = async () => {
    try {
      // Update each encoder that needs updating
      const encodersNeedingUpdate = encodersWithStatus.filter(e => 
        e.is_online && e.update_status === 'update_available'
      );
      
      for (const encoder of encodersNeedingUpdate) {
        await handleUpdateEncoderAgent(encoder.id);
      }
    } catch (_error) {
      // Failed to update all encoders - individual updates may have succeeded
    }
  };

  const handleDeleteEncoder = async () => {
    if (!encoderToDelete) return;
    
    try {
      await api.delete(`/api/encoders/${encoderToDelete.id}`);
      setDeleteConfirmOpen(false);
      setEncoderToDelete(null);
      // Refresh the list
      fetchData();
    } catch (_error) {
      // Failed to delete encoder - list will not be updated
    }
  };

  const handleResetStats = async () => {
    try {
      await api.post('/api/encoders/reset-stats');
      // Refresh the data to show reset stats
      fetchData();
    } catch (_error) {
      // Failed to reset stats - stats will remain unchanged
    }
  };

  const handleEditEncoder = (encoder) => {
    setEditingEncoder(encoder);
    setEditFormData({
      name: encoder.name,
      hostname: encoder.hostname,
      ip_address: encoder.ip_address || '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateEncoder = async () => {
    if (!editingEncoder) return;
    
    try {
      await api.put(`/api/encoders/${editingEncoder.id}`, editFormData);
      setEditDialogOpen(false);
      setEditingEncoder(null);
      // Refresh the list
      fetchData();
    } catch (_error) {
      // Failed to update encoder - dialog remains open
    }
  };



  const formatLastSeen = (lastHeartbeat, isOnline) => {
    if (!lastHeartbeat && !isOnline) return '-';
    if (!lastHeartbeat && isOnline) return 'Just now';
    
    const date = new Date(lastHeartbeat);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (isOnline && diffSecs < 60) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Encode Nodes</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Refresh encoder list and status" arrow placement="top">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          </Tooltip>
          {userInfo?.role?.toLowerCase() === 'admin' && (
            <Tooltip title="Reset all encoder statistics to zero" arrow placement="top">
              <Button
                variant="outlined"
                startIcon={<ResetIcon />}
                onClick={handleResetStats}
                color="warning"
              >
                Reset Stats
              </Button>
            </Tooltip>
          )}
          {updateStatus?.updates_available > 0 && userInfo?.role?.toLowerCase() === 'admin' && (
            <Tooltip title="Update all encoders to the latest version" arrow placement="top">
              <Button
                variant="contained"
                startIcon={<UpdateIcon />}
                onClick={handleUpdateAll}
              >
                Update All ({updateStatus.updates_available})
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Register a new encoder node to process watermarking jobs" arrow placement="top">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Add Encoder
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Statistics Summary */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Tooltip title="Total number of registered encoder nodes" arrow placement="top">
              <Paper sx={{ p: 2 }}>
                <Typography color="textSecondary" gutterBottom>
                  Total Encoders
                </Typography>
                <Typography variant="h4">{stats.total}</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Tooltip title="Encoders currently connected and ready to process jobs" arrow placement="top">
              <Paper sx={{ p: 2 }}>
                <Typography color="textSecondary" gutterBottom>
                  Online
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.online}
                </Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Tooltip title="Encoders actively processing watermarking jobs" arrow placement="top">
              <Paper sx={{ p: 2 }}>
                <Typography color="textSecondary" gutterBottom>
                  Processing
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.processing}
                </Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Tooltip title="Total watermarking jobs completed by all encoders" arrow placement="top">
              <Paper sx={{ p: 2 }}>
                <Typography color="textSecondary" gutterBottom>
                  Jobs Completed
                </Typography>
                <Typography variant="h4">{stats.total_jobs_completed}</Typography>
              </Paper>
            </Tooltip>
          </Grid>
        </Grid>
      )}

      {/* Update Status Summary */}
      {updateStatus && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Update Status</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Current Version</Typography>
              <Typography variant="h6">{updateStatus.current_version || 'Unknown'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Latest Version</Typography>
              <Typography variant="h6">{updateStatus.latest_version || 'Unknown'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Last Checked</Typography>
              <Typography variant="h6">
                {updateStatus.last_checked 
                  ? new Date(updateStatus.last_checked).toLocaleString()
                  : 'Never'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Updates Available</Typography>
              <Typography variant="h6" color={updateStatus.updates_available > 0 ? 'warning.main' : 'text.primary'}>
                {updateStatus.updates_available || 0}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Encoders Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="medium" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '12%' }}>
                  <Tooltip title="Encoder node name">
                    <span>Name</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '12%' }}>
                  <Tooltip title="Hostname or computer name">
                    <span>Host</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '12%' }}>
                  <Tooltip title="IP address of the encoder">
                    <span>IP Address</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '8%' }}>
                  <Tooltip title="Connection status">
                    <span>Status</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '10%' }}>
                  <Tooltip title="Time since last heartbeat">
                    <span>Last Seen</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ width: '6%' }}>
                  <Tooltip title="CPU usage percentage">
                    <span>CPU</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ width: '6%' }}>
                  <Tooltip title="Memory usage percentage">
                    <span>RAM</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ width: '6%' }}>
                  <Tooltip title="Currently processing a job">
                    <span>Active</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ width: '8%' }}>
                  <Tooltip title="Total jobs completed (failed)">
                    <span>Jobs</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '8%' }}>
                  <Tooltip title="Encoder agent software version">
                    <span>Version</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ width: '10%', whiteSpace: 'nowrap' }}>
                  <Tooltip title="Available actions">
                    <span>Actions</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && encodersWithStatus.length === 0 ? (
                <TableRow>
                  <TableCell 
                    colSpan={11} 
                    align="center"
                    sx={{ height: 300 }}
                  >
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%'
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : encodersWithStatus.length === 0 ? (
                <TableRow>
                  <TableCell 
                    colSpan={11} 
                    align="center"
                    sx={{ height: 300 }}
                  >
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%'
                      }}
                    >
                      <Typography color="text.secondary">
                        No encoder nodes configured. Click "Add Encoder" to add one.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                encodersWithStatus.map((encoder) => (
                  <TableRow key={encoder.id}>
                    <TableCell>{encoder.name}</TableCell>
                    <TableCell>{encoder.hostname}</TableCell>
                    <TableCell>{encoder.ip_address || '-'}</TableCell>
                    <TableCell>
                      {encoder.is_paused ? (
                        <Tooltip
                          title={encoder.current_job_id
                            ? `Paused — current job ${encoder.current_job_id} continues, no new work claimed`
                            : 'Paused — encoder is idle, not claiming new work'}
                          arrow placement="top"
                        >
                          <Chip
                            label={encoder.current_job_id ? 'draining' : 'paused'}
                            color="warning"
                            size="small"
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title={encoder.is_online ? 'Encoder is connected and ready' : 'Encoder is not connected'} arrow placement="top">
                          <Chip
                            label={encoder.is_online ? 'online' : 'offline'}
                            color={encoder.is_online ? 'success' : 'default'}
                            size="small"
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={encoder.is_online ? "text.primary" : "text.secondary"}>
                        {formatLastSeen(encoder.last_heartbeat, encoder.is_online)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {encoder.is_online ? (
                        <Typography 
                          variant="body2" 
                          color={encoder.cpu_usage > 80 ? "error" : encoder.cpu_usage > 60 ? "warning" : "text.primary"}
                          fontWeight={encoder.cpu_usage > 80 ? "bold" : "normal"}
                        >
                          {Math.round(encoder.cpu_usage || 0)}%
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {encoder.is_online ? (
                        <Typography 
                          variant="body2" 
                          color={encoder.memory_usage > 80 ? "error" : encoder.memory_usage > 60 ? "warning" : "text.primary"}
                          fontWeight={encoder.memory_usage > 80 ? "bold" : "normal"}
                        >
                          {Math.round(encoder.memory_usage || 0)}%
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {encoder.is_processing || encoder.current_job_id ? (
                        <CheckIcon color="success" fontSize="small" />
                      ) : (
                        <Typography variant="body2" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {encoder.jobs_completed || 0}
                      </Typography>
                      {encoder.jobs_failed > 0 && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', lineHeight: 1 }}>
                          ({encoder.jobs_failed} failed)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {encoder.agent_version || 'Unknown'}
                        </Typography>
                        {encoder.update_status === 'update_available' && (
                          <Tooltip title="Update Available">
                            <UpdateIcon color="warning" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                        <Tooltip title="Edit encoder name and details" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => handleEditEncoder(encoder)}
                            sx={{ 
                              color: 'action.active',
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Get installation command for this encoder" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => handleSetup(encoder)}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                          >
                            <SetupIcon />
                          </IconButton>
                        </Tooltip>
                        {encoder.is_online && (
                          <Tooltip title="View detailed performance metrics and history" arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/encoders/${encoder.id}/health`)}
                              sx={{
                                color: 'success.main',
                                '&:hover': { backgroundColor: 'action.hover' }
                              }}
                            >
                              <HealthIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip
                          title={encoder.is_paused
                            ? 'Resume — encoder will start claiming new work'
                            : encoder.current_job_id
                              ? 'Pause after current job — current job runs to completion, no new work claimed'
                              : 'Pause — encoder will stop claiming new work'}
                          arrow placement="top"
                        >
                          <IconButton
                            size="small"
                            onClick={async () => {
                              await api.put(`/api/encoders/${encoder.id}`, { is_paused: !encoder.is_paused });
                              dispatch(fetchEncoders());
                            }}
                            sx={{
                              color: encoder.is_paused ? 'success.main' : 'warning.main',
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                          >
                            {encoder.is_paused ? <PlayIcon /> : <PauseIcon />}
                          </IconButton>
                        </Tooltip>
                        {encoder.is_online && userInfo?.role?.toLowerCase() === 'admin' && (
                          <Tooltip title={encoder.update_status === 'update_available' ? "Update encoder to latest version" : "Force reinstall encoder software"} arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateEncoderAgent(encoder.id)}
                              disabled={updating[encoder.id]}
                              sx={{ 
                                color: encoder.update_status === 'update_available' ? 'warning.main' : 'action.active',
                                '&:hover': { backgroundColor: 'action.hover' }
                              }}
                            >
                              {updating[encoder.id] ? <CircularProgress size={20} /> : <UpdateIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {userInfo?.role?.toLowerCase() === 'admin' && (
                          <Tooltip title="Remove this encoder from the system" arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEncoderToDelete(encoder);
                                setDeleteConfirmOpen(true);
                              }}
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { backgroundColor: 'error.lighter' }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Encoder Node</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a new encoder node. After adding, click "Setup Agent" to install the encoder software.
          </Typography>
          <TextField
            margin="dense"
            label="Encoder Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Hostname"
            placeholder="encoder.local or encoder-1"
            fullWidth
            variant="outlined"
            value={formData.hostname}
            onChange={(e) => setFormData({ ...formData, hostname: e.target.value })}
          />
          <TextField
            margin="dense"
            label="IP Address (Optional)"
            placeholder="192.168.1.100"
            fullWidth
            variant="outlined"
            value={formData.ip_address}
            onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
            helperText="For reference only - encoder connects to server"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Add Encoder
          </Button>
        </DialogActions>
      </Dialog>

      <EncoderSetupDialog
        open={setupDialogOpen}
        onClose={() => {
          setSetupDialogOpen(false);
          setSelectedEncoder(null);
        }}
        encoder={selectedEncoder}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Encoder</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the encoder "{encoderToDelete?.name}" ({encoderToDelete?.hostname})?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The encoder will be removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteEncoder} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Encoder Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Encoder</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Encoder Name"
            fullWidth
            variant="outlined"
            value={editFormData.name}
            onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Hostname"
            fullWidth
            variant="outlined"
            value={editFormData.hostname}
            onChange={(e) => setEditFormData({ ...editFormData, hostname: e.target.value })}
          />
          <TextField
            margin="dense"
            label="IP Address"
            placeholder="192.168.1.100"
            fullWidth
            variant="outlined"
            value={editFormData.ip_address}
            onChange={(e) => setEditFormData({ ...editFormData, ip_address: e.target.value })}
            helperText="For reference only - encoder connects to server"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateEncoder} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Encoders;